import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ChannelType } from '@enums/ChannelType';
import { Channel } from '@models/Channel';
import { ChannelPlayback } from '@models/ChannelPlayback';
import { Country } from '@models/CountriesAndLanguages';

import { RootState } from '..';

import {
  SetChannelListPayload,
  SetChannelPlaybackPayload,
  SetChannelPlaybacksPayload,
} from './types';

type ChannelsState = {
  channelPlaybacks: ChannelPlayback[];
  channels: Channel[];
};

const initialState: ChannelsState = {
  channels: [],
  channelPlaybacks: [],
};

// Base selectors
const selectChannels = (state: RootState) => state.channels.channels;
const selectChannelPlaybacks = (state: RootState) => state.channels.channelPlaybacks;

// Memoized selectors for performance
export const isChannelMigrated = (portalId?: string) =>
  createSelector([selectChannelPlaybacks], channelPlaybacks =>
    channelPlaybacks.some(({ channelId }) => channelId === portalId)
  );

export const getFilteredChannels = (country?: Country, type?: ChannelType, migrated?: boolean) =>
  createSelector([selectChannels, selectChannelPlaybacks], (channels, channelPlaybacks) => {
    if (!country && !type && migrated == null) return channels;

    return channels?.filter(channel => {
      if (type && channel.type !== type) return false;
      if (country && !channel.countries.includes(country)) return false;
      if (migrated != null) {
        const isMigrated = channelPlaybacks.some(({ channelId }) => channelId === channel.portalId);
        return isMigrated === migrated;
      }
      return true;
    });
  });

// SLICE
export const channelsSlice = createSlice({
  name: 'page',
  initialState,
  reducers: {
    clearChannelList: () => ({
      channelPlaybacks: [],
      channels: [],
    }),

    setChannelList: (
      state,
      { payload: { channels = [] } }: PayloadAction<SetChannelListPayload>
    ) => {
      state.channels = channels;
    },

    setChannelPlaybacks: (
      state,
      { payload: { channelPlaybacks = [] } }: PayloadAction<SetChannelPlaybacksPayload>
    ) => {
      state.channelPlaybacks = channelPlaybacks;
    },

    setChannelPlayback: (
      state,
      { payload: { channelPlayback } }: PayloadAction<SetChannelPlaybackPayload>
    ) => {
      state.channelPlaybacks = [...state.channelPlaybacks, channelPlayback];
    },

    removeChannelPlayback: (state, { payload: channelId }: PayloadAction<string>) => {
      state.channelPlaybacks = state.channelPlaybacks?.filter(item => item.channelId !== channelId);
    },
  },
});

export const { actions: channelsActions } = channelsSlice;

export default channelsSlice.reducer;
